import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { auth } from '../lib/api'

export default function Pricing() {
  const navigate = useNavigate()
  const [billingPeriod, setBillingPeriod] = useState('yearly') // Default to yearly (better value)
  const [loading, setLoading] = useState(true)
  const [expandedFaq, setExpandedFaq] = useState(null)

  const monthlyPrice = 25
  const yearlyPrice = 240 // 20% discount
  const monthlySavings = (monthlyPrice * 12) - yearlyPrice

  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const isAuth = await auth.isAuthenticated()
        if (isAuth) {
          navigate('/billing')
          return
        }
      } catch (error) {
        console.error('Error checking user status:', error)
      } finally {
        setLoading(false)
      }
    }
    checkUserStatus()
  }, [navigate])

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ice-600 mx-auto"></div>
      </div>
    )
  }

  const features = [
    'Unlimited Leagues',
    'Unlimited Teams',
    'Unlimited Players',
    'Schedule Management',
    'Live Scoring & Standings',
    'Payment Tracking',
    'Sub Request System',
    'Playoff Brackets',
    'Custom Domain',
    'Announcements & Messaging',
  ]

  const faqs = [
    {
      q: 'What happens during the free trial?',
      a: 'Full access to all features for 14 days. Set up your organization, leagues, teams, and players completely free.',
    },
    {
      q: 'Are there any limits on players or teams?',
      a: 'No limits! Unlimited teams and players included. All players and captains access the system at no extra cost.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Yes! Cancel anytime and keep access until the end of your billing period.',
    },
    {
      q: 'What payment methods do you accept?',
      a: 'PayPal (credit cards, debit cards, PayPal balance, and Venmo).',
    },
  ]

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 transition-colors gap-1 mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
          Simple, Transparent Pricing
        </h1>
        <p className="text-slate-600 mt-1">
          Everything you need to manage your hockey organization
        </p>
      </div>

      {/* Main Content - Two Column on Desktop */}
      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        {/* Left: Pricing Card */}
        <div className="card p-6 border-2 border-ice-600 shadow-lg">
          {/* Billing Toggle */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  billingPeriod === 'monthly'
                    ? 'bg-ice-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  billingPeriod === 'yearly'
                    ? 'bg-ice-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Yearly
              </button>
            </div>
            {billingPeriod === 'yearly' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Save ${monthlySavings}/yr
              </span>
            )}
          </div>

          {/* Price */}
          <div className="text-center mb-6">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl sm:text-5xl font-bold text-slate-900">
                ${billingPeriod === 'monthly' ? monthlyPrice : yearlyPrice}
              </span>
              <span className="text-lg text-slate-600">
                /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
              </span>
            </div>
            {billingPeriod === 'yearly' && (
              <p className="text-sm text-green-600 font-medium mt-1">
                Just $20/month when paid annually
              </p>
            )}
          </div>

          {/* Features - Compact Grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6">
            {features.map((feature) => (
              <div key={feature} className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Link
            to="/login?signup=true&type=org_owner"
            className="btn-primary btn-lg w-full"
          >
            Start 14-Day Free Trial
          </Link>
          <p className="text-xs text-slate-500 text-center mt-2">
            No credit card required to start. Cancel anytime.
          </p>
        </div>

        {/* Right: FAQs */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <span className="font-medium text-slate-900 text-sm">{faq.q}</span>
                  <svg
                    className={`w-5 h-5 text-slate-500 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedFaq === idx && (
                  <div className="p-4 bg-white border-t">
                    <p className="text-sm text-slate-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Trust Elements */}
          <div className="mt-6 p-4 bg-ice-50 rounded-lg border border-ice-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-ice-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div>
                <p className="font-semibold text-ice-900 text-sm">14-Day Free Trial</p>
                <p className="text-xs text-ice-800 mt-1">
                  Try everything risk-free. Your trial includes full access to all features.
                  Subscribe via PayPal when you're ready.
                </p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="mt-4 text-center">
            <p className="text-sm text-slate-600">
              Questions? Email{' '}
              <a href="mailto:support@iceops.app" className="text-ice-600 hover:underline">
                support@iceops.app
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
